import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import {
  RateCardStatus,
  ReadRateCardDTO,
} from "@models/rate-card/entities/rateCard";
import { nameOfFactory } from "@utils";
import {
  SHAvatar,
  SHBox,
  SHImage,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { TextOverflowEllipsis } from "@pages/platform-analysis/components/text-overflow-ellipsis";
import { InformationButton } from "@components/buttons/information";
import { SHTextOverflowTooltip } from "@components/design-systems/sh-text-overflow-tooltip";
import { format } from "date-fns";
import { DateTimeFormat } from "@constants/format";
import {
  RateCardFilterOptions,
  RateCardFilterYesNoOptions,
} from "@pages/platform-analysis/constant";
import { StatusBadge } from "@components/status-badge";
import React from "react";

export const RateCardListColumnHealer = createColumnHelper<ReadRateCardDTO>();
export const RateCardListNameOfFactory = nameOfFactory<ReadRateCardDTO>();

export const getAnalysisRateCardListColumns = ({
  isAdviserGroup,
  renderItemActions,
}: {
  isAdviserGroup: boolean;
  renderItemActions: (itemData: ReadRateCardDTO) => React.ReactNode;
}): ColumnDef<ReadRateCardDTO, any>[] => {
  const columnHelper = RateCardListColumnHealer;
  const nameOf = RateCardListNameOfFactory;
  const cols: ColumnDef<ReadRateCardDTO, any>[] = [
    columnHelper.accessor((row) => row, {
      id: nameOf("name"),
      header: "Name of rate card",
      cell: ({ row: { original: rateCardData } }) =>
        renderRateCardName(rateCardData),
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "17%",
          minWidth: "200px",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Platform",
      id: nameOf("productId"),
      cell: ({ row: { original: rateCardData } }) =>
        renderPlatform(rateCardData),
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "15%",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      header: "Owner",
      id: nameOf("ownerName"),
      cell: ({ row: { original: rateCardData } }) => renderOwner(rateCardData),
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "12%",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor("lastModifiedDate", {
      header: "Last edit",
      cell: (props) => {
        const date = props.cell.getValue();
        return date ? format(new Date(date), DateTimeFormat) : "";
      },
      enableColumnFilter: false,
      meta: {
        sx: {
          width: "13%",
          minWidth: { xs: "130px" },
          padding: "0 0 0 25px !important",
        },
      },
    }),
    columnHelper.accessor("isAvailableForAnalysis", {
      header: isAdviserGroup
        ? "Available for my reviews"
        : "Available for my analysis",
      cell: (props) => (
        <SHTypography fontSize={13} lineHeight={"120%"} minWidth={"100px"}>
          {props.cell.getValue() ? "Yes" : "No"}
        </SHTypography>
      ),
      meta: {
        filterData: RateCardFilterYesNoOptions,
        sx: {
          width: { xs: "100px", md: "12%" },
          minWidth: "100px",
          padding: "0 5px !important",
        },
      },
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (props) => (
        <StatusBadge
          style={{ width: "88px", fontSize: 13 }}
          status={props.cell.getValue() as RateCardStatus}
        />
      ),
      meta: {
        filterData: RateCardFilterOptions,
        sx: {
          width: "10%",
          maxWidth: "120px",
          padding: "0 16px !important",
        },
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "actionCol",
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row: { original: rateCardData } }) =>
        renderItemActions(rateCardData),
      meta: {
        sx: {
          width: "10%",
        },
      },
    }),
  ];

  if (!isAdviserGroup) {
    const shareCol = columnHelper.accessor("sharedValue", {
      header: "Shared",
      cell: (props) => (
        <SHTypography fontSize={13} lineHeight={"120%"}>
          {props.cell.getValue()}
        </SHTypography>
      ),
      meta: {
        filterData: RateCardFilterYesNoOptions,
        sx: {
          width: "5%",
          maxWidth: "120px",
          padding: "0 16px !important",
        },
      },
    });
    cols.splice(5, 0, shareCol);
  }

  return cols;
};

const renderRateCardName = (rateCardData: ReadRateCardDTO): JSX.Element => (
  <SHStack direction={"row"}>
    <TextOverflowEllipsis fontWeight="bold" value={rateCardData.name ?? ""} />
    <SHBox
      sx={{
        display: "inline-flex",
        alignItems: "flex-end",
      }}
    >
      {rateCardData.notes && <InformationButton content={rateCardData.notes} />}
    </SHBox>
  </SHStack>
);

const renderPlatform = (rateCardData: ReadRateCardDTO) => (
  <SHStack direction={"row"} alignItems={"center"}>
    <SHImage
      width={"24px"}
      height={"24px"}
      style={{ objectFit: "contain" }}
      src={rateCardData.productAvatarUrl}
    />
    <SHStack direction={"column"} marginLeft={"5px"}>
      <SHTextOverflowTooltip
        tooltipContent={rateCardData.productName ?? ""}
        lineClamp={1}
        breakAnyWhere={true}
        tooltipPlacement={"top-start"}
        preventGetFitWidth
      >
        <SHTypography fontWeight={"bold"} lineHeight={"15px"}>
          {rateCardData.productName ?? ""}
        </SHTypography>
      </SHTextOverflowTooltip>

      <SHTextOverflowTooltip
        tooltipContent={rateCardData.subProductName ?? ""}
        lineClamp={1}
        breakAnyWhere={true}
        tooltipPlacement={"top-start"}
        preventGetFitWidth
      >
        <SHTypography
          sx={{
            minWidth: "100%",
            lineHeight: "15px",
          }}
        >
          {rateCardData.subProductName ?? ""}
        </SHTypography>
      </SHTextOverflowTooltip>
    </SHStack>
  </SHStack>
);

const renderOwner = (rateCardData: ReadRateCardDTO) => (
  <SHStack direction={"row"} alignItems={"center"}>
    <SHAvatar
      width={"24px"}
      height={"24px"}
      src={rateCardData.ownerAvatarUrl}
    />
    <TextOverflowEllipsis
      value={rateCardData.ownerName ?? ""}
      sx={{
        marginLeft: "5px",
        minWidth: "100%",
      }}
    />
  </SHStack>
);
